import { FormaPagamento, SituacaoRespostaCotacaoProduto } from 'px-business-components';
import currency from 'currency.js';
import { DEFAULT_PRECISION } from '@/utils/produto-utils';
import SituacaPrecoTarget from '@/enums/situacao-preco-target';
import {
  groupBy, first, last, maxBy, find, sortBy, sumBy,
  forEach,
} from 'lodash';

function splitPrazoMedio(prazo) {
  const prazos = prazo.split('/');
  const primeiraParcela = Number(first(prazos));
  const ultimaParcela = Number(last(prazos));
  return (primeiraParcela + ultimaParcela) / 2;
}

function getSituacaoPrecoTarget(produto) {
  const precision = produto.produto
    .cotacaoProduto.produto.casasDecimais || DEFAULT_PRECISION;

  const diffPreco = currency(
    produto.produto.precoUnitario, { precision },
  ).subtract(
    produto.produto
      .cotacaoProduto.produto.precoTarget, { precision },
  ).value;

  const doisPorcentoPrecoTarget = currency(
    produto.produto.cotacaoProduto.produto.precoTarget, { precision },
  ).multiply(
    0.02, { precision },
  ).value;

  if (diffPreco <= 0) {
    return SituacaPrecoTarget.keys.PRECO_ABAIXO.key;
  }
  if (diffPreco <= doisPorcentoPrecoTarget) {
    return SituacaPrecoTarget.keys.PRECO_ACIMA.key;
  }
  return SituacaPrecoTarget.keys.PRECO_MUITO_ACIMA.key;
}

function setPrazoMedio(produto) { /* eslint-disable no-param-reassign */
  if (produto.produto.situacao !== SituacaoRespostaCotacaoProduto.keys.RESPONDIDA.key) {
    produto.$prazoMedio = 0;
    return;
  }
  if (produto.formaPagamento === FormaPagamento.keys.ANTECIPADO.key) {
    produto.$prazoMedio = 0;
    return;
  }
  if (produto.formaPagamento === FormaPagamento.keys.A_VISTA.key) {
    produto.$prazoMedio = 1;
    return;
  }
  produto.$prazoMedio = splitPrazoMedio(produto.prazoPagamento.descricao);
}

function setMelhorPrazoPagamentoProperty(cotacoesProdutos) {
  const melhorPrazo = maxBy(cotacoesProdutos, '$prazoMedio');
  if (!melhorPrazo) {
    return;
  }

  const equalMelhoPreco = find(cotacoesProdutos,
    prod => prod.$prazoMedio === melhorPrazo.$prazoMedio
      && prod.produto.id !== melhorPrazo.produto.id
      && prod.$melhorPreco);

  if (equalMelhoPreco) {
    equalMelhoPreco.$melhorPrazoPagamento = true;
    return;
  }

  melhorPrazo.$melhorPrazoPagamento = true;
}

function setMelhorPrazoPagamento(produtos) {
  const grupos = groupBy(produtos, 'produto.cotacaoProduto.id');
  Object.keys(grupos)
    .forEach((key) => {
      const cotacoesProdutos = grupos[key];
      cotacoesProdutos.forEach(setPrazoMedio);
      setMelhorPrazoPagamentoProperty(cotacoesProdutos);
    });
}

function setMelhorFrete(produtos) {
  const grupos = groupBy(produtos, 'produto.cotacaoProduto.id');
  Object.keys(grupos)
    .forEach((key) => {
      const cotacoesProdutos = grupos[key];
      const [produtosSorted] = sortBy(cotacoesProdutos, ['frete.tipoFrete', 'frete.totalTransporte']);
      produtosSorted.$melhorFrete = true;
    });
}

const buildTotal = (total, array) => {
  const builtTotal = {
    id: total[0].id,
    valorMoedaConvertido: {
      total: sumBy(total, 'valorMoedaConvertido.total'),
      totalProdutos: sumBy(total, 'valorMoedaConvertido.totalProdutos'),
      totalIcms: sumBy(total, 'valorMoedaConvertido.totalIcms'),
      totalIpi: sumBy(total, 'valorMoedaConvertido.totalIpi'),
      totalDifal: sumBy(total, 'valorMoedaConvertido.totalDifal'),
    },
    produtos: total.flatMap(totalMap => totalMap.produtos.map(produto => produto)),
  };
  array.push(builtTotal);
};

export const buildTotais = (respostas, situacao) => {
  const builtTotais = [];
  const totais = respostas
    .filter(respota => respota.produtos
      .some(prod => !situacao?.length || situacao.includes(prod.cotacaoProduto.situacao)))
    .map(resposta => ({
      id: resposta.fornecedor.id,
      produtos: resposta.produtos,
      valorMoedaConvertido: {
        total: sumBy(resposta.produtos
          .filter(prod => prod.situacao === SituacaoRespostaCotacaoProduto.keys.RESPONDIDA.key), 'valorMoedaConvertido.total'),
        totalProdutos: sumBy(resposta.produtos
          .filter(prod => prod.situacao === SituacaoRespostaCotacaoProduto.keys.RESPONDIDA.key), 'valorMoedaConvertido.totalProdutos'),
        totalIcms: sumBy(resposta.produtos
          .filter(prod => prod.situacao === SituacaoRespostaCotacaoProduto.keys.RESPONDIDA.key), 'impostos.valorIcmsConvertido'),
        totalIpi: sumBy(resposta.produtos
          .filter(prod => prod.situacao === SituacaoRespostaCotacaoProduto.keys.RESPONDIDA.key), 'impostos.valorIpiConvertido'),
        totalDifal: sumBy(resposta.produtos
          .filter(prod => prod.situacao === SituacaoRespostaCotacaoProduto.keys.RESPONDIDA.key), 'impostos.valorDifalConvertido'),
      },
    }));

  const temp = groupBy(totais, 'id');

  forEach(temp, value => buildTotal(value, builtTotais));

  return builtTotais;
};

export {
  setMelhorPrazoPagamento,
  setMelhorFrete,
  getSituacaoPrecoTarget,
};
